import type {
	AssignedPharmacyItem,
	AssignedUser,
	AssignPharmacyRequest,
	AuditLogEntry,
	CompanyGroup,
	DynamicRole,
	EvaluationResult,
	FranchiseAccountResult,
	FranczyzaImportMapping,
	FranczyzaImportReport,
	GroupRule,
	LoginRequest,
	LoginResponse,
	OrgGroup,
	OrgGroupCreateRequest,
	OrgGroupMembersRequest,
	OrgGroupUpdateRequest,
	OrgGroupVisibilityResponse,
	OrgGroupVisibilityRule,
	OrgGroupVisibilityUpsertRequest,
	PaginatedResponse,
	PharmacyGroup,
	PharmacyItem,
	PharmacyMember,
	PharmacyUpdateRequest,
	ProductScope,
	RefreshResponse,
	Role,
	SqlConsoleCompletionsResponse,
	SqlConsoleKillQueryRequest,
	SqlConsoleKillQueryResponse,
	SqlConsoleQueryRequest,
	SqlConsoleQueryResponse,
	SqlConsoleSavedQuery,
	SqlConsoleSavedQueryInput,
	SqlConsoleSchemaTreeResponse,
	SqlConsoleTableDetailsResponse,
	Tenant,
	TenantCreateRequest,
	TenantUpdateRequest,
	User,
	UserCreateRequest,
	UserUpdateRequest,
} from "./auth-types";
import type { ElementId, VisibilityRule } from "./visibility-types";

export class ApiError extends Error {
	constructor(
		message: string,
		public readonly status: number,
		public readonly code?: string,
		public readonly details?: Record<string, unknown>,
	) {
		super(message);
		this.name = "ApiError";
	}
}

/**
 * Buduje ApiError z odpowiedzi HTTP. Czyta body RAZ jako tekst i probuje JSON.
 *
 * Gdy backend zwraca AppError JSON `{ error, code, details }` — uzywamy go.
 * Gdy body NIE jest JSON-em (timeout proxy 502/504, 413 za duzy payload, panic
 * bez body, blad gateway HTML) — zamiast generycznego "Unknown error", ktory
 * maskowal przyczyne, zwracamy precyzyjne `HTTP <status> <statusText>` plus
 * krotki fragment body (o ile nie jest HTML-em). Latwiej zdiagnozowac na prod.
 */
export async function buildApiError(response: Response): Promise<ApiError> {
	const status = response.status;
	const statusLabel = `HTTP ${status}${response.statusText ? ` ${response.statusText}` : ""}`;
	const rawBody = await response.text().catch(() => "");

	if (rawBody.trim() !== "") {
		try {
			const parsed = JSON.parse(rawBody) as {
				error?: string;
				code?: string;
				details?: Record<string, unknown>;
			};
			if (parsed && typeof parsed === "object" && typeof parsed.error === "string") {
				return new ApiError(parsed.error, status, parsed.code, parsed.details);
			}
		} catch {
			// body nie jest JSON — fallback na status + fragment ponizej
		}

		const collapsed = rawBody.replace(/\s+/g, " ").trim();
		// Pomijamy fragment dla odpowiedzi HTML (strony bledow proxy) — sam status
		// niesie informacje, a HTML zasmiecalby toast.
		if (!collapsed.startsWith("<")) {
			return new ApiError(`${statusLabel}: ${collapsed.slice(0, 200)}`, status);
		}
	}

	return new ApiError(statusLabel, status);
}

// ── Tryb embedded (panel Asystenta w WebView agenta) ─────────────────────────
// Agent ładuje web /asystent w WebView i wstrzykuje init-scriptem Tauri obiekt
// `window.__FM_PANEL__ = { token, idfirm, apiBase }`: token = zawężony device-JWT
// (rola pharmacy_device, scope wymuszony per apteka po stronie backendu), apiBase
// = backend agenta. Dzięki temu web i panel agenta to JEDEN kod — zmiany UI lecą
// Vercelem bez przebudowy agenta.
export interface EmbedPanel {
	token: string;
	/** idfirm stałej apteki; `0` dla canary (apteka wybierana w panelu). */
	idfirm: number;
	apiBase?: string;
	/** Nazwa apteki do nagłówka panelu (stanowisko apteczne); brak dla canary. */
	pharmacyName?: string;
	/** `true` = urządzenie canary: panel pokazuje picker aptek zamiast jednej. */
	isCanary?: boolean;
}

export function readEmbedPanel(): EmbedPanel | null {
	if (typeof window === "undefined") return null;
	const p = (window as unknown as { __FM_PANEL__?: unknown }).__FM_PANEL__;
	if (
		p &&
		typeof (p as EmbedPanel).token === "string" &&
		typeof (p as EmbedPanel).idfirm === "number"
	) {
		return p as EmbedPanel;
	}
	return null;
}

/// Czy aplikacja działa jako osadzony panel agenta (chowa chrome, przypina scope).
export function isEmbedded(): boolean {
	return readEmbedPanel() !== null;
}

/// Wywołaj natywną komendę agenta (Tauri) z osadzonego panelu — pin/zamknij okna,
/// pobranie drzewa aptek dla canary. Mostek IPC jest wpuszczony przez capability
/// `asystent-panel-remote` (tylko nasza domena). Poza panelem (zwykła przeglądarka)
/// `window.__TAURI__` nie istnieje → no-op (`null`), więc kod współdzielony jest bezpieczny.
export async function panelInvoke<T = unknown>(
	cmd: string,
	args?: Record<string, unknown>,
): Promise<T | null> {
	const tauri = (
		window as unknown as {
			__TAURI__?: {
				core?: { invoke?: (cmd: string, args?: Record<string, unknown>) => Promise<unknown> };
			};
		}
	).__TAURI__;
	const invoke = tauri?.core?.invoke;
	if (typeof invoke !== "function") return null;
	try {
		return (await invoke(cmd, args)) as T;
	} catch (e) {
		console.warn(`[panel] invoke ${cmd} failed:`, e);
		return null;
	}
}

/// Wyemituj zdarzenie Tauri z osadzonego panelu (np. „panel:open-leaflet" → agent
/// otwiera ulotkę w osobnym oknie). Core-event jest dozwolony dla zdalnej strony
/// (capability), w przeciwieństwie do custom commands (ACL). Poza panelem (brak
/// `window.__TAURI__`) → no-op (`false`), więc kod współdzielony jest bezpieczny.
export async function panelEmit(event: string, payload?: unknown): Promise<boolean> {
	const tauri = (
		window as unknown as {
			__TAURI__?: { event?: { emit?: (event: string, payload?: unknown) => Promise<void> } };
		}
	).__TAURI__;
	const emit = tauri?.event?.emit;
	if (typeof emit !== "function") return false;
	try {
		await emit(event, payload);
		return true;
	} catch (e) {
		console.warn(`[panel] emit ${event} failed:`, e);
		return false;
	}
}

function getDefaultApiUrl(): string {
	if (typeof window === "undefined") {
		return "http://localhost:3000/api";
	}
	// Embedded: backend wskazany przez agenta (jego ws_url → http base + /api).
	const embed = readEmbedPanel();
	if (embed?.apiBase) return embed.apiBase;

	const protocol = window.location.protocol === "https:" ? "https:" : "http:";
	const host = window.location.hostname || "localhost";
	return `${protocol}//${host}:3000/api`;
}

export const BASE_URL = import.meta.env.VITE_API_URL ?? getDefaultApiUrl();
const SQL_CONSOLE_BASE_URL = import.meta.env.VITE_SQL_CONSOLE_API_URL ?? BASE_URL;

let _accessToken: string | null = null;

// Embedded bootstrap: ustaw wstrzyknięty token OD RAZU (przed pierwszym apiFetch),
// bo w WebView nie ma httpOnly refresh-cookie z którego web normalnie startuje.
{
	const _embed = readEmbedPanel();
	if (_embed) _accessToken = _embed.token;
}
let _currentUser: User | null = null;
let _tenantOverrideId: number | null = null;

// ─── Silent refresh mutex ────────────────────────────────────────────────────
// Wszystkie miejsca (route guard `ensureAuthenticated`, AuthProvider bootstrap,
// 401 retry w apiFetch) musza przechodzic przez ten mutex. Inaczej parallel
// /auth/refresh z tym samym single-use refresh cookie wyscigowo uniewaznia
// siebie nawzajem i zostaje _accessToken = null — backend zwraca wtedy
// "Tenant not identified" na kolejnych zapytaniach.
let _refreshPromise: Promise<RefreshResponse | null> | null = null;

export async function silentRefresh(): Promise<RefreshResponse | null> {
	if (_refreshPromise) return _refreshPromise;

	_refreshPromise = (async () => {
		try {
			const base = import.meta.env.VITE_API_URL ?? getDefaultApiUrl();
			const res = await fetch(`${base}/auth/refresh`, {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
			});
			if (!res.ok) return null;
			const data = (await res.json()) as RefreshResponse;
			_accessToken = data.accessToken;
			window.dispatchEvent(
				new CustomEvent("auth:token-refreshed", { detail: { expiresIn: data.expiresIn } }),
			);
			return data;
		} catch {
			return null;
		} finally {
			_refreshPromise = null;
		}
	})();

	return _refreshPromise;
}

// ─── Tryb podgladu: marker sesji + brama re-impersonacji ─────────────────────
// Podglad przezywa F5 wylacznie przez ten marker w sessionStorage — token po
// przeladowaniu strony nalezy do OPERATORA, bo `/auth/refresh` mintuje przez
// `issue_access_token`, ktore nie zna flagi `impersonating`.
//
// Tenant JEST czescia tozsamosci celu: `users.user_id` to IDENTITY w schemacie
// per-org, wiec to samo id istnieje w kazdym tenancie jako INNA osoba. Bez
// `targetTenantId` backend robi `unwrap_or(user.tenant_id)` i podglada przypadkowa
// osobe z tenanta operatora.
const IMPERSONATION_USER_KEY = "impersonating_user_id";
const IMPERSONATION_TENANT_KEY = "impersonating_tenant_id";
const IMPERSONATION_STARTED_KEY = "impersonating_started_at";

export interface ImpersonationTarget {
	userId: number;
	tenantId?: number;
}

export function setImpersonationMarker(target: ImpersonationTarget, startedAt: number): void {
	sessionStorage.setItem(IMPERSONATION_USER_KEY, String(target.userId));
	sessionStorage.setItem(IMPERSONATION_STARTED_KEY, String(startedAt));
	if (target.tenantId === undefined) sessionStorage.removeItem(IMPERSONATION_TENANT_KEY);
	else sessionStorage.setItem(IMPERSONATION_TENANT_KEY, String(target.tenantId));
}

export function clearImpersonationMarker(): void {
	sessionStorage.removeItem(IMPERSONATION_USER_KEY);
	sessionStorage.removeItem(IMPERSONATION_TENANT_KEY);
	sessionStorage.removeItem(IMPERSONATION_STARTED_KEY);
}

export function readImpersonationMarker(): ImpersonationTarget | null {
	const userId = Number(sessionStorage.getItem(IMPERSONATION_USER_KEY));
	if (!Number.isFinite(userId) || userId <= 0) return null;
	const rawTenant = sessionStorage.getItem(IMPERSONATION_TENANT_KEY);
	const tenantId = rawTenant === null ? undefined : Number(rawTenant);
	return { userId, tenantId: Number.isFinite(tenantId) ? tenantId : undefined };
}

// Mutex, NIE cache. Zerowany w `finally` dokladnie jak `_refreshPromise` powyzej —
// deduplikuje tylko rownolegly wyscig (bootstrap AuthProvidera vs `beforeLoad`
// routera po F5). Trzymanie rozwiazanego promise'a bylo blednym cache'em: po
// trafieniu pomijalo przypisanie `_accessToken`, wiec kolejny guard szedl do
// `/auth/me` tokenem operatora — czyli dokladnie ta dziura, ktora brama zamyka.
let _impersonationPromise: Promise<RefreshResponse | null> | null = null;
// Chroni przed rekurencja: `/admin/impersonation/start` idzie przez `apiFetch`,
// ktorego retry po 401 wola `refreshSession()`, ktore znow chcialoby tu wejsc.
let _impersonationInFlight = false;
// Token podgladu wydany przez brame i jego cel. Nie jest to cache z komentarza wyzej:
// liczy sie tylko, gdy BIEZACY `_accessToken` to wciaz ten token — `/auth/refresh`
// podmienia go na token operatora i wymusza nowy start. Bez tego guard po F5 wolal
// brame drugi raz juz z tokenem podgladu (`refreshSession`, potem `loadUser`), backend
// odmawial startu z podgladu (403), a `catch` nizej czyscil marker (FARAMSTER-BACKEND-75).
let _impersonationSession: {
	accessToken: string;
	expiresAt: number;
	target: ImpersonationTarget;
} | null = null;

function activeImpersonation(target: ImpersonationTarget): RefreshResponse | null {
	const session = _impersonationSession;
	if (
		!session ||
		session.accessToken !== _accessToken ||
		session.target.userId !== target.userId ||
		session.target.tenantId !== target.tenantId
	) {
		return null;
	}
	const expiresIn = Math.floor((session.expiresAt - Date.now()) / 1000);
	return expiresIn > 0 ? { accessToken: session.accessToken, expiresIn } : null;
}

export async function ensureImpersonation(): Promise<RefreshResponse | null> {
	const target = readImpersonationMarker();
	if (!target) return null;
	if (_impersonationPromise) return _impersonationPromise;
	const active = activeImpersonation(target);
	if (active) return active;

	_impersonationPromise = (async () => {
		_impersonationInFlight = true;
		try {
			const res = await adminApi.startImpersonation(target.userId, target.tenantId);
			_accessToken = res.accessToken;
			_impersonationSession = {
				accessToken: res.accessToken,
				expiresAt: Date.now() + res.expiresIn * 1000,
				target,
			};
			return { accessToken: res.accessToken, expiresIn: res.expiresIn };
		} catch {
			// Podgladany user zdezaktywowany / brak uprawnien / zly tenant — porzuc
			// podglad zamiast zostawiac sesje w stanie "UI mowi podglad, token mowi operator".
			clearImpersonationMarker();
			return null;
		} finally {
			_impersonationInFlight = false;
			_impersonationPromise = null;
		}
	})();

	return _impersonationPromise;
}

/**
 * Jedyny poprawny sposob odswiezenia sesji z poziomu aplikacji.
 *
 * `silentRefresh()` samo w sobie ZAWSZE oddaje token operatora, wiec wywolane
 * w trakcie podgladu po cichu przywraca pelne uprawnienia przy zywym markerze.
 * Ta funkcja domyka podglad po odswiezeniu, zeby retry po 401, zapis profilu i
 * powrot do karty nie otwieraly tej samej dziury trzema roznymi drzwiami.
 */
export async function refreshSession(): Promise<RefreshResponse | null> {
	const refreshed = await silentRefresh();
	if (!refreshed) return null;
	// Nie zapetlaj sie przez wlasne zadanie startu impersonacji.
	if (_impersonationInFlight) return refreshed;
	return (await ensureImpersonation()) ?? refreshed;
}

/**
 * Po 401 odswieza sesje i powtarza zadanie; `null`, gdy sesji nie da sie odswiezyc.
 *
 * Drugie podejscie dla ponowienia, ktore dotarlo do backendu juz po wygasnieciu
 * swiezego tokena: Safari wstrzymal je w usypianej karcie i wyslal po wybudzeniu
 * z tym samym naglowkiem (FARMASTER-FRONTEND-45: refresh 13:42, 401 na ponowienie
 * 14:16 przy TTL 15 min). `send` musi czytac `_accessToken` przy kazdym wywolaniu.
 */
async function retryWithRefreshedSession(send: () => Promise<Response>): Promise<Response | null> {
	let response: Response | null = null;
	for (let attempt = 0; attempt < 2; attempt++) {
		// refreshSession, NIE silentRefresh — samo /auth/refresh oddaje token
		// operatora, wiec retry w trakcie podgladu leciałby z pelnymi uprawnieniami.
		if (!(await refreshSession())) return null;
		response = await send();
		if (response.status !== 401) break;
	}
	return response;
}

export function getTokenExpiry(): number | null {
	if (!_accessToken) return null;
	try {
		const base64 = _accessToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
		const payload = JSON.parse(atob(base64));
		return payload.exp ?? null;
	} catch {
		return null;
	}
}

export function setAccessToken(token: string | null): void {
	_accessToken = token;
}

export function getAccessToken(): string | null {
	return _accessToken;
}

export function setCurrentUser(user: User | null): void {
	_currentUser = user;
}

export function getCurrentUser(): User | null {
	return _currentUser;
}

export function setTenantOverride(id: number | null): void {
	_tenantOverrideId = id;
}

export function getTenantOverride(): number | null {
	return _tenantOverrideId;
}

// ── Maintenance auto-redirect ──────────────────────────────────────────────
// Gdy backend bombarduje 502/503/504 lub timeoutuje (DB host down, deploy w toku),
// po N kolejnych awariach przekierowujemy uzytkownika na /maintenance.html.
// Maintenance page ma auto-refresh co 30s — gdy backend ozyje, user wraca sam.

const MAINTENANCE_FAILURE_THRESHOLD = 3;
let _consecutiveBackendFailures = 0;

function markBackendFailure(): void {
	_consecutiveBackendFailures += 1;
	if (_consecutiveBackendFailures >= MAINTENANCE_FAILURE_THRESHOLD) {
		redirectToMaintenance();
	}
}

function markBackendSuccess(): void {
	_consecutiveBackendFailures = 0;
}

function redirectToMaintenance(): void {
	if (typeof window === "undefined") return;
	if (window.location.pathname === "/maintenance.html") return;
	try {
		sessionStorage.setItem(
			"maintenance_redirect_from",
			window.location.pathname + window.location.search,
		);
	} catch {
		// sessionStorage moze byc wylaczony (Safari private) — ignorujemy
	}
	window.location.replace("/maintenance.html");
}

export type ApiFetchOptions = RequestInit & {
	/** Optional telemetry must not refresh auth or affect the application's outage state. */
	telemetry?: boolean;
};

export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
	return apiFetchWithBase<T>(BASE_URL, path, options);
}

export async function apiFetchBlob(
	path: string,
	options: RequestInit = {},
): Promise<{ blob: Blob; response: Response }> {
	return apiFetchBlobWithBase(BASE_URL, path, options);
}

async function apiFetchWithBase<T>(
	baseUrl: string,
	path: string,
	options: ApiFetchOptions = {},
): Promise<T> {
	const { telemetry = false, ...requestOptions } = options;
	// FormData detection (Codex review v2): browser sam ustawia multipart/form-data
	// + boundary — jesli dopiszemy Content-Type: application/json body sie nie
	// sparsuje po stronie axum. apiFetch zachowuje 401 silent refresh + tenant
	// override dla multipart upload (certyfikaty XAdES).
	const isFormData = options.body instanceof FormData;
	const headers: Record<string, string> = {
		"X-Requested-With": "XMLHttpRequest",
		...((options.headers as Record<string, string>) ?? {}),
	};
	if (!isFormData) {
		headers["Content-Type"] = "application/json";
	}

	if (_accessToken) {
		headers.Authorization = `Bearer ${_accessToken}`;
	}

	if (_tenantOverrideId !== null) {
		headers["X-Tenant-Override"] = String(_tenantOverrideId);
	}

	let response: Response;
	try {
		response = await fetch(`${baseUrl}${path}`, {
			...requestOptions,
			credentials: "include",
			headers,
		});
	} catch (err) {
		// Network error (offline, DNS, TLS, fetch abort) — backend failure dla maintenance redirect.
		if (!telemetry) markBackendFailure();
		throw err;
	}

	// 502/503/504 — upstream/gateway down. Counter; >=3 redirectuje na /maintenance.html.
	if (response.status >= 502 && response.status <= 504) {
		const error = await buildApiError(response);
		if (telemetry) throw error;
		if (error.status === 503 && error.code === "DATA_PREPARING") {
			// Backend responds correctly; the report's first snapshot is not ready yet.
			markBackendSuccess();
		} else {
			markBackendFailure();
		}
		throw error;
	}
	if (!telemetry) markBackendSuccess();

	// 401 -> najpierw probujemy silent refresh (refresh-cookie jest http-only,
	// niezalezne od _accessToken), dopiero gdy odswiezenie sie nie uda
	// emitujemy session-expired. WAZNE: warunek NIE moze byc `&& _accessToken` —
	// race miedzy zakladkami albo offline-restored fetch moze zostawic
	// _accessToken=null przy wciaz waznym refresh cookie. Bez prob refreshu
	// frontend wyrzucal "Tenant not identified" (Sentry FRONTEND-1D / Z) zamiast
	// cicho odswiezyc sesje (auth-api.ts:71-75 opisuje pelen scenariusz).
	// Wyjatek: 401 z /auth/login to bledne dane logowania, nie wygasla sesja —
	// formularz ma pokazac ogolny komunikat backendu, a nie „Sesja wygasla”.
	if (response.status === 401 && path !== "/auth/login") {
		// A telemetry retry could attribute an old batch to a newly logged-in user.
		if (telemetry) throw await buildApiError(response);
		// WebView panelu nie ma refresh-cookie. Poproś helper o odtworzenie okna —
		// nowy WebView dostanie świeży, zawężony panel-JWT z lokalnej usługi.
		if (isEmbedded() && (await panelEmit("panel:auth-expired"))) {
			throw new Error("Odnawiam sesję panelu");
		}
		const retryResponse = await retryWithRefreshedSession(() =>
			fetch(`${baseUrl}${path}`, {
				...requestOptions,
				credentials: "include",
				headers: { ...headers, Authorization: `Bearer ${_accessToken}` },
			}),
		);
		if (retryResponse) {
			if (!retryResponse.ok) {
				throw await buildApiError(retryResponse);
			}
			if (retryResponse.status === 204 || retryResponse.status === 205) {
				return undefined as T;
			}
			return parseJsonBody<T>(retryResponse);
		}
		window.dispatchEvent(new CustomEvent("auth:session-expired"));
		// Swiadomie zwykly Error, nie ApiError(401), tak jak "Odnawiam sesję panelu"
		// wyzej: React Query ponawia go raz (`lib/query-client-defaults.ts`), a drugie
		// refreshSession moze wygrac wyscig kart o jednorazowe refresh cookie.
		throw new Error("Sesja wygasla");
	}

	if (!response.ok) {
		throw await buildApiError(response);
	}

	if (response.status === 204 || response.status === 205) {
		return undefined as T;
	}

	return parseJsonBody<T>(response);
}

async function parseJsonBody<T>(response: Response): Promise<T> {
	const text = await response.text();
	if (!text) {
		throw new Error(`Serwer zwrocil pusta odpowiedz (${response.status} ${response.url})`);
	}
	return JSON.parse(text) as T;
}

async function apiFetchBlobWithBase(
	baseUrl: string,
	path: string,
	options: RequestInit = {},
): Promise<{ blob: Blob; response: Response }> {
	const isFormData = options.body instanceof FormData;
	const headers: Record<string, string> = {
		"X-Requested-With": "XMLHttpRequest",
		...((options.headers as Record<string, string>) ?? {}),
	};
	if (!isFormData) {
		headers["Content-Type"] = "application/json";
	}

	if (_accessToken) {
		headers.Authorization = `Bearer ${_accessToken}`;
	}

	if (_tenantOverrideId !== null) {
		headers["X-Tenant-Override"] = String(_tenantOverrideId);
	}

	const doFetch = (requestHeaders: Record<string, string>) =>
		fetch(`${baseUrl}${path}`, {
			...options,
			credentials: "include",
			headers: requestHeaders,
		});

	let response = await doFetch(headers);

	if (response.status === 401) {
		// Jak wyzej: podglad musi przezyc retry, inaczej blob leci jako operator.
		const retryResponse = await retryWithRefreshedSession(() => {
			const retryHeaders: Record<string, string> = {
				...headers,
				Authorization: _accessToken ? `Bearer ${_accessToken}` : "",
			};
			if (!retryHeaders.Authorization) {
				delete retryHeaders.Authorization;
			}
			return doFetch(retryHeaders);
		});
		if (!retryResponse) {
			window.dispatchEvent(new CustomEvent("auth:session-expired"));
			throw new Error("Sesja wygasla");
		}
		response = retryResponse;
	}

	if (!response.ok) {
		throw await buildApiError(response);
	}

	return {
		blob: await response.blob(),
		response,
	};
}

async function sqlConsoleFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
	return apiFetchWithBase<T>(SQL_CONSOLE_BASE_URL, path, options);
}

export const authApi = {
	login: (data: LoginRequest) =>
		apiFetch<LoginResponse>("/auth/login", { method: "POST", body: JSON.stringify(data) }),

	logout: () => apiFetch<void>("/auth/logout", { method: "POST" }),

	refreshToken: () => apiFetch<RefreshResponse>("/auth/refresh", { method: "POST" }),

	googleAuthorize: () => `${BASE_URL}/auth/google/authorize`,

	getMe: () => apiFetch<User>("/auth/me"),

	updateMe: (data: { name: string }) =>
		apiFetch<{ message: string }>("/auth/me", {
			method: "PATCH",
			body: JSON.stringify(data),
		}),

	changePassword: (data: { currentPassword: string; newPassword: string }) =>
		apiFetch<{ message: string }>("/auth/me/password", {
			method: "POST",
			body: JSON.stringify(data),
		}),

	requestPasswordReset: (email: string) =>
		apiFetch<void>("/auth/password-reset/request", {
			method: "POST",
			body: JSON.stringify({ email }),
		}),

	confirmPasswordReset: (token: string, newPassword: string, tenantSlug: string) =>
		apiFetch<void>("/auth/password-reset/confirm", {
			method: "POST",
			body: JSON.stringify({ token, newPassword, tenantSlug }),
		}),

	emailLog: {
		list: (queryString: string) =>
			apiFetch<{ data: unknown[]; total: number }>(`/admin/email-log?${queryString}`),
	},
};

interface RawUser {
	user_id: number;
	tenant_id?: number;
	email: string;
	name: string;
	is_active: boolean;
	status?: string;
	invited_at?: string;
	roles: Role[];
	created_at: string;
	last_login_at: string | null;
	google_sub?: string;
	pharmacy_group_ids?: number[];
	product_scopes?: Array<{ scope_type: string; scope_value: string }>;
	org_groups?: Array<{ group_id: number; name: string }>;
	assigned_pharmacies?: PharmacyMember[] | null;
}

interface RawPaginatedUsers {
	data: RawUser[];
	total: number;
	next_cursor?: number | string;
}

function mapUser(raw: RawUser): User {
	return {
		id: raw.user_id,
		tenantId: raw.tenant_id ?? 0,
		email: raw.email,
		name: raw.name,
		isActive: raw.is_active,
		status: raw.status as User["status"],
		invitedAt: raw.invited_at,
		roles: raw.roles,
		createdAt: raw.created_at,
		lastLoginAt: raw.last_login_at ?? undefined,
		googleSub: raw.google_sub,
		pharmacyGroupIds: raw.pharmacy_group_ids,
		productScopes: raw.product_scopes?.map((s) => ({
			scopeType: s.scope_type as "producer" | "brand",
			scopeValue: s.scope_value,
		})),
		orgGroups: raw.org_groups?.map((g) => ({ groupId: g.group_id, name: g.name })),
		assignedPharmacies: raw.assigned_pharmacies === undefined ? undefined : raw.assigned_pharmacies,
	};
}

interface RawAuditLogEntry {
	event_id: number;
	tenant_id: number;
	actor_user_id?: number;
	actor_email?: string;
	actor_name?: string;
	target_user_id?: number;
	target_email?: string;
	target_name?: string;
	event_type: AuditLogEntry["eventType"];
	ip?: string;
	user_agent?: string;
	details?: Record<string, unknown>;
	occurred_at: string;
}

function mapAuditEntry(raw: RawAuditLogEntry): AuditLogEntry {
	return {
		eventId: raw.event_id,
		tenantId: raw.tenant_id,
		actorUserId: raw.actor_user_id,
		actorEmail: raw.actor_email,
		actorName: raw.actor_name,
		targetUserId: raw.target_user_id,
		targetEmail: raw.target_email,
		targetName: raw.target_name,
		eventType: raw.event_type,
		ip: raw.ip,
		userAgent: raw.user_agent,
		details: raw.details,
		occurredAt: raw.occurred_at,
	};
}

interface RawVisibilityRule {
	ruleId: number;
	role: string;
	elementId: string;
	visible: boolean;
	createdAt: string;
	updatedAt: string;
}

interface VisibilityRuleResponse {
	ruleId: number;
	role: string;
	elementId: string;
	visible: boolean;
	createdAt: string;
	updatedAt: string;
}

function mapVisibilityRule(raw: RawVisibilityRule): VisibilityRule {
	return {
		ruleId: raw.ruleId,
		role: raw.role as Role,
		elementId: raw.elementId as ElementId,
		visible: raw.visible,
		createdAt: raw.createdAt,
		updatedAt: raw.updatedAt,
	};
}

export const adminApi = {
	getUsers: async (params?: {
		search?: string;
		role?: Role;
		isActive?: boolean;
		limit?: number;
		cursor?: string;
		overrideTenantId?: number;
	}): Promise<PaginatedResponse<User>> => {
		const qs = new URLSearchParams();
		if (params?.search) qs.set("search", params.search);
		if (params?.role) qs.set("role", params.role);
		if (params?.isActive !== undefined) qs.set("is_active", String(params.isActive));
		if (params?.limit) qs.set("limit", String(params.limit));
		if (params?.cursor) qs.set("cursor", params.cursor);
		if (params?.overrideTenantId) qs.set("override_tenant_id", String(params.overrideTenantId));
		const raw = await apiFetch<RawPaginatedUsers>(`/admin/users?${qs}`);
		return {
			data: raw.data.map(mapUser),
			total: raw.total,
			nextCursor: raw.next_cursor != null ? String(raw.next_cursor) : undefined,
		};
	},

	getUser: async (id: number): Promise<User> => {
		if (id == null) throw new Error("getUser: id is required");
		const raw = await apiFetch<RawUser>(`/admin/users/${id}`);
		return mapUser(raw);
	},

	createUser: (data: UserCreateRequest) =>
		apiFetch<User>("/admin/users", { method: "POST", body: JSON.stringify(data) }),

	franczyzaImport: (
		file: File,
		dryRun: boolean,
		mapping?: FranczyzaImportMapping,
		force?: boolean,
	) => {
		const fd = new FormData();
		fd.append("file", file);
		const params = new URLSearchParams({ dry_run: String(dryRun) });
		if (force) params.set("force", "true");
		if (mapping) {
			params.set("idfirm_col", String(mapping.idfirmCol));
			params.set("caretaker_col", String(mapping.caretakerCol));
			params.set("header_row", String(mapping.headerRow));
			params.set("parent_role", mapping.parentRole);
		}
		return apiFetch<FranczyzaImportReport>(`/admin/users/franczyza-import?${params}`, {
			method: "POST",
			body: fd,
		});
	},

	updateUser: (id: number, data: UserUpdateRequest) =>
		apiFetch<User>(`/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(data) }),

	assignRoles: (userId: number, roles: Role[]) =>
		apiFetch<void>(`/admin/users/${userId}/roles`, {
			method: "POST",
			body: JSON.stringify({ roles }),
		}),

	revokeRole: (userId: number, role: string) =>
		apiFetch<void>(`/admin/users/${userId}/roles/${role}`, { method: "DELETE" }),

	setProductScopes: (userId: number, scopes: ProductScope[]) =>
		apiFetch<void>(`/admin/users/${userId}/product-scopes`, {
			method: "PUT",
			body: JSON.stringify({ scopes }),
		}),

	getPharmacyGroups: async (): Promise<PharmacyGroup[]> => {
		interface RawGroup {
			group_id: number;
			name: string;
			group_type: string;
			rules_json?: GroupRule;
			member_count: number;
			pharmacies?: Array<{ idfirm: number; name: string }>;
			created_at: string;
			ksef_token?: { tokenId: number; nip: string; companyName: string | null } | null;
		}
		const raw = await apiFetch<RawGroup[]>("/admin/pharmacy-groups");
		return raw.map((g) => ({
			groupId: g.group_id,
			tenantId: 0,
			name: g.name,
			groupType: g.group_type as "static" | "dynamic",
			rulesJson: g.rules_json,
			memberCount: g.member_count,
			pharmacies: (g.pharmacies ?? []).map((p) => ({
				idfirm: p.idfirm,
				name: p.name,
			})),
			createdAt: g.created_at,
			ksefToken: g.ksef_token ?? null,
		}));
	},

	createPharmacyGroup: (
		data: Omit<PharmacyGroup, "groupId" | "tenantId" | "memberCount" | "createdAt">,
	) =>
		apiFetch<PharmacyGroup>("/admin/pharmacy-groups", {
			method: "POST",
			body: JSON.stringify(data),
		}),

	updatePharmacyGroup: (id: number, data: { name?: string; rulesJson?: GroupRule | null }) =>
		apiFetch<PharmacyGroup>(`/admin/pharmacy-groups/${id}`, {
			method: "PATCH",
			body: JSON.stringify(data),
		}),

	deletePharmacyGroup: (id: number) =>
		apiFetch<void>(`/admin/pharmacy-groups/${id}`, { method: "DELETE" }),

	getPharmacies: async (search?: string): Promise<PharmacyMember[]> => {
		const qs = search ? `?search=${encodeURIComponent(search)}` : "";
		const raw = await apiFetch<Array<{ idfirm: number; name: string }>>(`/admin/pharmacies${qs}`);
		return raw.map((p) => ({ idfirm: p.idfirm, name: p.name }));
	},

	listPharmacyOptions: async (): Promise<PharmacyMember[]> => {
		const raw = await apiFetch<Array<{ idfirm: number; name: string }>>("/admin/pharmacy-options");
		return raw.map((p) => ({ idfirm: p.idfirm, name: p.name }));
	},

	listPharmacies: (options?: { includeInactive?: boolean }): Promise<PharmacyItem[]> => {
		const qs = options?.includeInactive ? "?include_inactive=true" : "";
		return apiFetch<PharmacyItem[]>(`/admin/pharmacies${qs}`);
	},

	updatePharmacy: (id: number, data: PharmacyUpdateRequest): Promise<PharmacyItem> =>
		apiFetch<PharmacyItem>(`/admin/pharmacies/${id}`, {
			method: "PATCH",
			body: JSON.stringify(data),
		}),

	listPharmacyUsers: (idfirm: number): Promise<AssignedUser[]> =>
		apiFetch<AssignedUser[]>(`/admin/pharmacies/by-idfirm/${idfirm}/users`),

	/** dry_run=true robi transakcje z rollbackiem — wolaj TYLKO na jawny klik. */
	createFranchiseAccount: (idfirm: number, dryRun: boolean): Promise<FranchiseAccountResult> =>
		apiFetch<FranchiseAccountResult>(
			`/admin/pharmacies/${idfirm}/franchise-account?dry_run=${dryRun}`,
			{ method: "POST" },
		),

	listPharmacyFieldValues: (field: "kierownik" | "region"): Promise<string[]> =>
		apiFetch<string[]>(`/admin/pharmacies/field-values?field=${field}`),

	addPharmacyToGroup: (groupId: number, idfirm: number) =>
		apiFetch<void>(`/admin/pharmacy-groups/${groupId}/members`, {
			method: "POST",
			body: JSON.stringify({ idfirm }),
		}),

	removePharmacyFromGroup: (groupId: number, idfirm: number) =>
		apiFetch<void>(`/admin/pharmacy-groups/${groupId}/members`, {
			method: "DELETE",
			body: JSON.stringify({ idfirm }),
		}),

	getUsersByPharmacyGroup: async (groupId: number): Promise<User[]> => {
		const raw = await apiFetch<Array<{ id: number; email: string; name: string; roles: string[] }>>(
			`/admin/pharmacy-groups/${groupId}/users`,
		);
		return raw.map((u) => ({
			id: u.id,
			tenantId: 0,
			email: u.email,
			name: u.name,
			roles: u.roles as Role[],
		}));
	},

	addUserToPharmacyGroup: (groupId: number, userId: number) =>
		apiFetch<void>(`/admin/pharmacy-groups/${groupId}/users`, {
			method: "POST",
			body: JSON.stringify({ userId }),
		}),

	removeUserFromPharmacyGroup: (groupId: number, userId: number) =>
		apiFetch<void>(`/admin/pharmacy-groups/${groupId}/users`, {
			method: "DELETE",
			body: JSON.stringify({ userId }),
		}),

	getRolesByPharmacyGroup: async (groupId: number): Promise<string[]> => {
		const raw = await apiFetch<Array<{ role: string }>>(`/admin/pharmacy-groups/${groupId}/roles`);
		return raw.map((r) => r.role);
	},

	addRoleToPharmacyGroup: (groupId: number, role: string) =>
		apiFetch<void>(`/admin/pharmacy-groups/${groupId}/roles`, {
			method: "POST",
			body: JSON.stringify({ role }),
		}),

	removeRoleFromPharmacyGroup: (groupId: number, role: string) =>
		apiFetch<void>(`/admin/pharmacy-groups/${groupId}/roles`, {
			method: "DELETE",
			body: JSON.stringify({ role }),
		}),

	evaluatePharmacyGroup: (groupId: number) =>
		apiFetch<EvaluationResult>(`/admin/pharmacy-groups/${groupId}/evaluate`, {
			method: "POST",
		}),

	previewEvaluatePharmacyGroup: (groupId: number) =>
		apiFetch<EvaluationResult>(`/admin/pharmacy-groups/${groupId}/evaluate`),

	getAuditLog: async (params?: {
		eventType?: string;
		actorId?: number;
		targetId?: number;
		from?: string;
		to?: string;
		limit?: number;
		cursor?: string;
	}): Promise<{ data: AuditLogEntry[]; count: number }> => {
		const qs = new URLSearchParams();
		if (params?.eventType) qs.set("event_type", params.eventType);
		if (params?.actorId) qs.set("actor_id", String(params.actorId));
		if (params?.targetId) qs.set("target_id", String(params.targetId));
		if (params?.from) qs.set("from", params.from);
		if (params?.to) qs.set("to", params.to);
		if (params?.limit) qs.set("limit", String(params.limit));
		if (params?.cursor) qs.set("cursor", params.cursor);
		const raw = await apiFetch<{ data: RawAuditLogEntry[]; count: number }>(
			`/admin/audit-log?${qs}`,
		);
		return { data: raw.data.map(mapAuditEntry), count: raw.count };
	},

	forceLogout: (userId: number) =>
		apiFetch<{ revokedCount: number }>(`/admin/users/${userId}/force-logout`, { method: "POST" }),

	resetUserPassword: (userId: number) =>
		apiFetch<void>(`/admin/users/${userId}/reset-password`, { method: "POST" }),

	setUserPassword: (userId: number, newPassword: string) =>
		apiFetch<{ message: string; revokedSessions?: number }>(`/admin/users/${userId}/set-password`, {
			method: "POST",
			body: JSON.stringify({ newPassword }),
		}),

	inviteUser: (userId: number) =>
		apiFetch<{ message: string; status: string }>(`/admin/users/${userId}/invite`, {
			method: "POST",
		}),

	getTenants: async (): Promise<Tenant[]> => {
		interface RawTenant {
			tenant_id: number;
			name: string;
			slug: string;
			is_active: boolean;
			allowed_google_domains: string[];
		}
		const raw = await apiFetch<RawTenant[]>("/admin/tenants");
		return raw.map((t) => ({
			tenantId: t.tenant_id,
			name: t.name,
			slug: t.slug,
			isActive: t.is_active,
			allowedGoogleDomains: t.allowed_google_domains,
		}));
	},

	getTenant: async (id: number): Promise<Tenant> => {
		interface RawTenant {
			tenant_id: number;
			name: string;
			slug: string;
			is_active: boolean;
			allowed_google_domains: string[];
		}
		const raw = await apiFetch<RawTenant>(`/admin/tenants/${id}`);
		return {
			tenantId: raw.tenant_id,
			name: raw.name,
			slug: raw.slug,
			isActive: raw.is_active,
			allowedGoogleDomains: raw.allowed_google_domains,
		};
	},

	createTenant: (data: TenantCreateRequest) =>
		apiFetch<Tenant>("/admin/tenants", { method: "POST", body: JSON.stringify(data) }),

	updateTenant: (id: number, data: TenantUpdateRequest) =>
		apiFetch<Tenant>(`/admin/tenants/${id}`, { method: "PATCH", body: JSON.stringify(data) }),

	// `targetTenantId` jest OBOWIAZKOWY przy podgladzie miedzy tenantami: backend
	// robi `unwrap_or(user.tenant_id)`, a `users.user_id` to IDENTITY per-org, wiec
	// bez niego podglad trafia w przypadkowa osobe o tym samym id w tenancie operatora.
	startImpersonation: (targetUserId: number, targetTenantId?: number) =>
		apiFetch<{
			accessToken: string;
			expiresIn: number;
			impersonating: { userId: number; email: string; name: string };
		}>("/admin/impersonation/start", {
			method: "POST",
			body: JSON.stringify(
				targetTenantId === undefined ? { targetUserId } : { targetUserId, targetTenantId },
			),
		}),

	stopImpersonation: () => apiFetch<void>("/admin/impersonation/stop", { method: "DELETE" }),

	getImpersonationTargets: () =>
		apiFetch<{
			data: {
				userId: number;
				email: string;
				name: string;
				status: string | null;
				isActive: boolean;
				lastLoginAt: string | null;
				roles: string[];
				pharmacies: string[];
			}[];
		}>("/admin/impersonation/targets"),

	getVisibilityRules: async (role?: string): Promise<VisibilityRule[]> => {
		const params = role ? `?role=${encodeURIComponent(role)}` : "";
		const raw = await apiFetch<{ data: RawVisibilityRule[] }>(`/admin/visibility-rules${params}`);
		return raw.data.map(mapVisibilityRule);
	},

	getMyVisibilityRules: async (): Promise<VisibilityRule[]> => {
		const raw = await apiFetch<{ data: RawVisibilityRule[] }>("/visibility-rules/me");
		return raw.data.map(mapVisibilityRule);
	},

	upsertVisibilityRule: (rule: {
		role: string;
		elementId: string;
		visible: boolean;
	}): Promise<VisibilityRuleResponse> =>
		apiFetch<VisibilityRuleResponse>("/admin/visibility-rules", {
			method: "PUT",
			body: JSON.stringify(rule),
		}),

	upsertVisibilityRuleForAllRoles: (rule: { elementId: string; visible: boolean }): Promise<void> =>
		apiFetch<void>("/admin/visibility-rules/bulk", {
			method: "PUT",
			body: JSON.stringify(rule),
		}),

	deleteVisibilityRule: (ruleId: number): Promise<void> =>
		apiFetch<void>(`/admin/visibility-rules/${ruleId}`, { method: "DELETE" }),

	getAllUsers: async (): Promise<User[]> => {
		const all: User[] = [];
		let cursor: string | undefined;
		do {
			const page = await adminApi.getUsers({ limit: 100, cursor });
			all.push(...page.data);
			cursor = page.nextCursor;
		} while (cursor !== undefined);
		return all;
	},

	// Org Groups API
	getOrgGroups: async (): Promise<OrgGroup[]> => {
		interface RawOrgGroup {
			groupId: number;
			parentGroupId?: number | null;
			name: string;
			description?: string | null;
			color?: string | null;
			memberCount: number;
			children?: RawOrgGroup[];
			members?: Array<{ userId: number; name: string; email: string; roles: Role[] }>;
			createdAt: string;
		}
		const mapGroup = (g: RawOrgGroup): OrgGroup => ({
			groupId: g.groupId,
			parentGroupId: g.parentGroupId ?? undefined,
			name: g.name,
			description: g.description ?? undefined,
			color: g.color ?? undefined,
			memberCount: g.memberCount,
			children: g.children?.map(mapGroup),
			members: g.members?.map((m) => ({
				id: m.userId,
				name: m.name,
				email: m.email,
				roles: m.roles,
			})),
			createdAt: g.createdAt,
		});

		const raw = await apiFetch<RawOrgGroup[]>("/admin/org-groups");
		return raw.map(mapGroup);
	},

	getOrgGroup: async (id: number): Promise<OrgGroup> => {
		interface RawOrgGroup {
			groupId: number;
			parentGroupId?: number | null;
			name: string;
			description?: string | null;
			color?: string | null;
			memberCount: number;
			children?: RawOrgGroup[];
			members?: Array<{ userId: number; name: string; email: string; roles: Role[] }>;
			createdAt: string;
		}
		const g = await apiFetch<RawOrgGroup>(`/admin/org-groups/${id}`);
		return {
			groupId: g.groupId,
			parentGroupId: g.parentGroupId ?? undefined,
			name: g.name,
			description: g.description ?? undefined,
			color: g.color ?? undefined,
			memberCount: g.memberCount,
			children: g.children?.map((c) => ({
				groupId: c.groupId,
				parentGroupId: c.parentGroupId ?? undefined,
				name: c.name,
				description: c.description ?? undefined,
				color: c.color ?? undefined,
				memberCount: c.memberCount,
				createdAt: c.createdAt,
			})),
			members: g.members?.map((m) => ({
				id: m.userId,
				name: m.name,
				email: m.email,
				roles: m.roles,
			})),
			createdAt: g.createdAt,
		};
	},
	createOrgGroup: (data: OrgGroupCreateRequest) =>
		apiFetch<OrgGroup>("/admin/org-groups", {
			method: "POST",
			body: JSON.stringify(data),
		}),

	updateOrgGroup: (id: number, data: OrgGroupUpdateRequest) =>
		apiFetch<OrgGroup>(`/admin/org-groups/${id}`, {
			method: "PATCH",
			body: JSON.stringify(data),
		}),

	deleteOrgGroup: (id: number) => apiFetch<void>(`/admin/org-groups/${id}`, { method: "DELETE" }),

	setOrgGroupMembers: (groupId: number, data: OrgGroupMembersRequest) =>
		apiFetch<void>(`/admin/org-groups/${groupId}/members`, {
			method: "PUT",
			body: JSON.stringify(data),
		}),

	getRoles: async (): Promise<DynamicRole[]> => {
		const raw =
			await apiFetch<
				Array<{
					role: string;
					parentRole?: string | null;
					name?: string | null;
					description?: string | null;
					sortOrder: number;
					isSystem: boolean;
					isAdmin?: boolean;
					is_admin?: boolean;
				}>
			>("/admin/roles");
		return raw.map((r) => ({
			role: r.role,
			parentRole: r.parentRole ?? undefined,
			name: r.name ?? undefined,
			description: r.description ?? undefined,
			sortOrder: r.sortOrder,
			isSystem: r.isSystem,
			isAdmin: r.isAdmin ?? r.is_admin ?? false,
		}));
	},

	createSubrole: (parentRole: string, name: string, roleKey?: string) => {
		return apiFetch<DynamicRole>("/admin/roles/subroles", {
			method: "POST",
			body: JSON.stringify({
				parentRole,
				name,
				roleKey,
			}),
		});
	},

	updateDynamicRole: (
		role: string,
		data: { displayName?: string; description?: string; sortOrder?: number },
	) =>
		apiFetch<DynamicRole>(`/admin/roles/${encodeURIComponent(role)}`, {
			method: "PUT",
			body: JSON.stringify(data),
		}),

	deleteDynamicRole: (role: string) =>
		apiFetch<void>(`/admin/roles/${encodeURIComponent(role)}`, { method: "DELETE" }),

	getUsersByRole: async (role: string): Promise<User[]> => {
		const raw = await apiFetch<Array<{ id: number; email: string; name: string; roles: string[] }>>(
			`/admin/roles/${encodeURIComponent(role)}/users`,
		);
		return raw.map((u) => ({
			id: u.id,
			tenantId: 0,
			email: u.email,
			name: u.name,
			roles: u.roles as Role[],
		}));
	},

	setUserRole: (userId: number, role: string) =>
		apiFetch<void>(`/admin/users/${userId}/role`, {
			method: "PUT",
			body: JSON.stringify({ role }),
		}),

	getPharmaciesByCompany: () => apiFetch<CompanyGroup[]>("/admin/pharmacies/by-company"),

	getPharmacyCountsByRole: () => apiFetch<Record<string, number>>("/admin/roles/pharmacy-counts"),

	getSiblingAssignedPharmacyNames: (role: string) =>
		apiFetch<{ nips: string[]; idfirms: number[] }>(
			`/admin/roles/${encodeURIComponent(role)}/sibling-assigned-pharmacy-names`,
		),

	getAssignedPharmacies: (role: string) =>
		apiFetch<AssignedPharmacyItem[]>(`/admin/roles/${encodeURIComponent(role)}/pharmacies`),

	assignPharmacyToRole: (role: string, data: AssignPharmacyRequest) =>
		apiFetch<AssignedPharmacyItem>(`/admin/roles/${encodeURIComponent(role)}/pharmacies`, {
			method: "POST",
			body: JSON.stringify(data),
		}),

	unassignPharmacyFromRole: (role: string, groupId: number) =>
		apiFetch<void>(`/admin/roles/${encodeURIComponent(role)}/pharmacies/${groupId}`, {
			method: "DELETE",
		}),

	getOrgGroupVisibility: (groupId?: number) => {
		const qs = groupId ? `?groupId=${groupId}` : "";
		return apiFetch<{ data: OrgGroupVisibilityRule[] }>(`/admin/org-group-visibility${qs}`);
	},

	upsertOrgGroupVisibility: (data: OrgGroupVisibilityUpsertRequest) =>
		apiFetch<OrgGroupVisibilityRule>("/admin/org-group-visibility", {
			method: "PUT",
			body: JSON.stringify(data),
		}),

	deleteOrgGroupVisibility: (groupId: number, elementId: string) =>
		apiFetch<void>(`/admin/org-group-visibility/${groupId}/${elementId}`, { method: "DELETE" }),

	getMyOrgVisibilityRules: () => apiFetch<OrgGroupVisibilityResponse>("/visibility-rules/me/org"),
};

export function logExport(data: {
	panel: string;
	filters?: Record<string, unknown>;
	dateRange?: { from?: string; to?: string };
	rowCount: number;
}): void {
	apiFetch<void>("/admin/audit/export-event", {
		method: "POST",
		body: JSON.stringify(data),
	}).catch((e) => {
		console.debug("[audit] Export event logging failed:", e);
	});
}

export interface CreateFeedbackIssueRequest {
	issueType: "bug" | "idea";
	title: string;
	description: string;
	email?: string;
	screenshot?: string;
	additionalDetails?: string;
	context: {
		currentPath: string;
		userAgent: string;
		screenResolution: string;
		timestamp: string;
		userName?: string;
		userEmail?: string;
		userRoles?: string;
		errorCode?: string;
		errorMessage?: string;
		errorStack?: string;
	};
}

export interface CreateFeedbackIssueResponse {
	issueUrl: string;
	issueNumber: number;
}

export const feedbackApi = {
	createIssue: (data: CreateFeedbackIssueRequest) =>
		apiFetch<CreateFeedbackIssueResponse>("/feedback/issues", {
			method: "POST",
			body: JSON.stringify(data),
		}),
};

export const sqlConsoleApi = {
	getSchemaTree: () => sqlConsoleFetch<SqlConsoleSchemaTreeResponse>("/sql-console/tree"),
	getTableDetails: (schemaName: string, tableName: string) =>
		sqlConsoleFetch<SqlConsoleTableDetailsResponse>(
			`/sql-console/table-details?schemaName=${encodeURIComponent(schemaName)}&tableName=${encodeURIComponent(tableName)}`,
		),
	executeQuery: (data: SqlConsoleQueryRequest) =>
		sqlConsoleFetch<SqlConsoleQueryResponse>("/sql-console/query", {
			method: "POST",
			body: JSON.stringify(data),
		}),
	killQuery: (data: SqlConsoleKillQueryRequest) =>
		sqlConsoleFetch<SqlConsoleKillQueryResponse>("/sql-console/query/kill", {
			method: "POST",
			body: JSON.stringify(data),
		}),
	/** Cały wynik zapytania Target albo Source (`db: "source"`) jako CSV (bez limitu wierszy siatki). */
	exportCsv: (data: SqlConsoleQueryRequest) =>
		apiFetchBlobWithBase(SQL_CONSOLE_BASE_URL, "/sql-console/query/csv", {
			method: "POST",
			body: JSON.stringify(data),
		}),
	getCompletions: () => sqlConsoleFetch<SqlConsoleCompletionsResponse>("/sql-console/completions"),
	listSaved: (db: "target" | "apteka") =>
		sqlConsoleFetch<{ queries: SqlConsoleSavedQuery[] }>(`/sql-console/saved?db=${db}`),
	createSaved: (data: SqlConsoleSavedQueryInput) =>
		sqlConsoleFetch<SqlConsoleSavedQuery>("/sql-console/saved", {
			method: "POST",
			body: JSON.stringify(data),
		}),
	updateSaved: (id: number, data: SqlConsoleSavedQueryInput) =>
		sqlConsoleFetch<SqlConsoleSavedQuery>(`/sql-console/saved/${id}`, {
			method: "PUT",
			body: JSON.stringify(data),
		}),
	deleteSaved: (id: number) =>
		sqlConsoleFetch<{ deleted: number }>(`/sql-console/saved/${id}`, { method: "DELETE" }),
};

export interface DeployResponse {
	message: string;
	triggered: boolean;
}

export interface LastDeployInfo {
	triggeredBy: string | null;
	triggeredAt: string | null; // ISO 8601 string
}

export interface DeployLogResponse {
	lines: string[];
}

export interface ContainerInfo {
	name: string;
	status: string;
	state: string;
	startedAt: string | null;
	uptime: string | null;
	image: string | null;
}

export interface SchemaMigrationStatus {
	schemaName: string;
	currentVersion: number;
	expectedVersion: number;
	upToDate: boolean;
	lastAppliedAt: string | null;
}

export interface TechStackEntry {
	/** `rust`, `crate:<nazwa>`, `npm:<nazwa>`, `node`, `postgresql`, `ext:<nazwa>`, `qdrant`. */
	key: string;
	name: string;
	version: string;
}

export interface TechStackResponse {
	backend: TechStackEntry[];
	database: TechStackEntry[];
	qdrantVersion: string | null;
}

/** `unknown` = brak odczytu z endoflife.date albo nieznany cykl — UI pokazuje „brak danych”. */
export type SupportStatus = "supported" | "ending" | "eol" | "unknown";

export interface SupportInfo {
	product: string;
	cycle: string | null;
	/** Koniec standardowego wsparcia (YYYY-MM-DD); null = bez terminu albo nieznany. */
	eol: string | null;
	/** Koniec wsparcia rozszerzonego (Ubuntu ESM). */
	extendedEol: string | null;
	/** Status liczony od wsparcia rozszerzonego, bo jest wlaczone (Ubuntu Pro). */
	extendedActive: boolean;
	status: SupportStatus;
	warnMonths: number;
}

export interface TechStackLatestResponse {
	/** Klucz jak w `TechStackEntry.key`; brak klucza = rejestr nie odpowiedzial. */
	latest: Record<string, string>;
	/** Status wsparcia producenta; starszy backend go nie wysyla. */
	support?: Record<string, SupportInfo>;
}

export interface ServerOs {
	id: string;
	prettyName: string;
	versionId: string;
	codename: string;
}

export interface ServerPatches {
	collectedAt: string;
	pending: number;
	security: number;
	/** Poprawki bezpieczenstwa dostepne tylko z Ubuntu Pro (ESM). */
	esmUnavailable: number;
	securityPendingSince: string | null;
	rebootRequired: boolean;
	lastAptUpdateAt: string | null;
	lastUnattendedRunAt: string | null;
	unattendedEnabled: boolean | null;
	proAttached: boolean | null;
	esmInfra: boolean | null;
	esmApps: boolean | null;
	/** Zaleglosci do reakcji; pusta lista = poprawki w porzadku. */
	warnings: string[];
}

export interface TechStackServer {
	key: string;
	label: string;
	status: "ok" | "noData";
	error: string | null;
	lastScrapeAt: string | null;
	os: ServerOs | null;
	osSupport: SupportInfo | null;
	dockerVersion: string | null;
	dockerLatest: string | null;
	dockerSupport: SupportInfo | null;
	/** null = brak swiezych danych o poprawkach (powod w `patchesError`). */
	patches: ServerPatches | null;
	patchesError: string | null;
}

export interface TechStackServersResponse {
	measuredAt: string;
	servers: TechStackServer[];
}

export const deployApi = {
	triggerDeploy: () =>
		apiFetch<DeployResponse>("/admin/deploy", {
			method: "POST",
		}),
	getLastDeploy: () => apiFetch<LastDeployInfo>("/admin/deploy/last"),
	getDeployLog: () => apiFetch<DeployLogResponse>("/admin/deploy/log"),
	getSchemaMigrationStatus: () =>
		apiFetch<SchemaMigrationStatus>("/admin/schema-migrations/status"),
	getContainers: () => apiFetch<{ containers: ContainerInfo[] }>("/admin/containers"),
	getTechStack: () => apiFetch<TechStackResponse>("/admin/tech-stack"),
	/** `frontendVersions`: `klucz@wersja` skladnikow znanych tylko z buildu (status wsparcia). */
	getTechStackLatest: (npmPackages: string[], frontendVersions: string[]) =>
		apiFetch<TechStackLatestResponse>(
			`/admin/tech-stack/latest?${new URLSearchParams({
				npm: npmPackages.join(","),
				frontend: frontendVersions.join(","),
			})}`,
		),
	getTechStackServers: () => apiFetch<TechStackServersResponse>("/admin/tech-stack/servers"),
};

// ─── Zajetosc dyskow ─────────────────────────────────────────────────────────

export interface FilesystemUsage {
	device: string;
	mountpoint: string;
	fstype: string;
	totalBytes: number;
	usedBytes: number;
	availableBytes: number;
	usedPercent: number;
}

export interface DiskSource {
	key: string;
	label: string;
	/** "ok" albo "error" — przy "error" `filesystems` jest zawsze puste. */
	status: string;
	error: string | null;
	lastSuccessAt: string | null;
	filesystems: FilesystemUsage[];
}

export interface DiskUsageResponse {
	measuredAt: string;
	sources: DiskSource[];
}

export const diskUsageApi = {
	getDiskUsage: () => apiFetch<DiskUsageResponse>("/admin/disk-usage"),
};

// ─── Qdrant status ───────────────────────────────────────────────────────────

export interface QdrantCollection {
	name: string;
	pointsCount: number;
	status: string;
}

export interface QdrantIndexingEntry {
	collection: string;
	tenantSchema: string | null;
	indexedCount: number;
	durationMs: number;
	status: string;
	errorMessage: string | null;
	startedAt: string;
}

export interface QdrantStatusResponse {
	qdrantOnline: boolean;
	collections: QdrantCollection[];
	currentlyRunning: QdrantIndexingEntry[];
	recentIndexings: QdrantIndexingEntry[];
	recentErrors: QdrantIndexingEntry[];
}

export const qdrantApi = {
	getStatus: () => apiFetch<QdrantStatusResponse>("/admin/qdrant-status"),
};
